import { useMemo, useState } from "react";
import { Loader, Button } from "@/ui/atoms";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { Save, AlertCircle } from "lucide-react";
import { cn } from "@/utils/utils";
import { formatDealStatusLabel } from "@/utils/formatDealStatus";
import { checkDeadlineDay, resolveDeadlineReferenceMonth } from "@/utils/deadlineDay";
import { getNow } from "@/utils/dateService";
import type { TApiUser, TUpdateDealRequest } from "@/apis/proda_finance/types";
import type { TDeal } from "@/store";

type TDealInfoFormProps = {
    deal: TDeal;
    users: TApiUser[];
    usersLoading: boolean;
    finalDestinationOptions: { value: string; label: string }[];
    segment2MappingTypes: { value: string; label: string }[];
    segment4MappingTypes: { value: string; label: string }[];
    segment5MappingTypes: { value: string; label: string }[];
    segment6MappingTypes: { value: string; label: string }[];
    segment7MappingTypes: { value: string; label: string }[];
    dealFileTypeLabels: Record<string, string>;
    formatStartPeriod: (startPeriod: string | null) => string;
    canEdit?: boolean;
    /** Renaming a deal is Product-Owner-only, separate from the general edit right. */
    canEditName?: boolean;
    onSave: (data: TUpdateDealRequest) => Promise<void>;
};

export function DealInfoForm({
    deal,
    users,
    usersLoading,
    finalDestinationOptions,
    segment2MappingTypes,
    segment4MappingTypes,
    segment5MappingTypes,
    segment6MappingTypes,
    segment7MappingTypes,
    dealFileTypeLabels,
    formatStartPeriod,
    canEdit = true,
    canEditName = false,
    onSave,
}: TDealInfoFormProps) {
    const [name, setName] = useState<string>(deal.name);
    // Baseline of the last persisted name, advanced after each successful save so
    // an unchanged field is never re-sent (the backend rejects a rename from a
    // caller without DEAL_NAME_EDIT, and the `deal` prop is not refreshed between
    // saves).
    const [savedName, setSavedName] = useState<string>(deal.name);
    const [isActive, setIsActive] = useState<boolean>(deal.isActive);
    // Baseline of the last persisted status. Compared against `isActive` to decide
    // whether to send `is_active` on save, and advanced after each successful save
    // so a toggle → save → re-toggle → save sequence within one session still sends
    // the new value (the `deal` prop is not refreshed between saves).
    const [savedIsActive, setSavedIsActive] = useState<boolean>(deal.isActive);
    const [financeUserIds, setFinanceUserIds] = useState<string[]>(deal.financeUserIds ?? []);
    const [finalDestination, setFinalDestination] = useState<string>(deal.finalFileDestination);
    const [deadlineDay, setDeadlineDay] = useState<number | string>(deal.monthlyDeadlineDay ?? "");
    const [segment2MappingType, setSegment2MappingType] = useState(deal.segment2MappingType);
    const [segment4MappingType, setSegment4MappingType] = useState(deal.segment4MappingType);
    const [segment5MappingType, setSegment5MappingType] = useState(deal.segment5MappingType);
    const [segment6MappingType, setSegment6MappingType] = useState(deal.segment6MappingType);
    const [segment7MappingType, setSegment7MappingType] = useState(deal.segment7MappingType);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const deadlineNum =
        typeof deadlineDay === "number" ? deadlineDay : parseInt(deadlineDay as string, 10);
    const deadlineValid =
        !deadlineDay || (!isNaN(deadlineNum) && deadlineNum >= 1 && deadlineNum <= 28);
    // Advisory only: the deadline recurs monthly, so we check it against the deal's
    // start period (or next month once that is in the past). Never blocks saving.
    const deadlineWarning = useMemo(
        () =>
            deadlineValid && deadlineDay !== ""
                ? checkDeadlineDay(
                      deadlineNum,
                      resolveDeadlineReferenceMonth(deal.startPeriod, getNow()),
                  )
                : null,
        [deadlineValid, deadlineDay, deadlineNum, deal.startPeriod],
    );
    const nameEditable = canEdit && canEditName;
    const trimmedName = name.trim();
    const nameValid = !nameEditable || (trimmedName.length > 0 && trimmedName.length <= 255);

    const handleSave = async () => {
        if (!deadlineValid || !nameValid) return;
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await onSave({
                // Only send the name on an actual rename: the backend gates that
                // on DEAL_NAME_EDIT and ignores an unchanged value anyway.
                ...(nameEditable && trimmedName !== savedName ? { name: trimmedName } : {}),
                // Only send the active flag when the user actually changed it since
                // the last persisted value, so an unrelated save never re-pushes a
                // stale value and reverts a status change made elsewhere (e.g. the
                // deals-table toggle). The backend treats an omitted is_active as
                // "no change".
                ...(isActive !== savedIsActive ? { is_active: isActive } : {}),
                finance_user_ids: financeUserIds,
                final_file_destination:
                    finalDestination as TUpdateDealRequest["final_file_destination"],

                monthly_deadline_day: deadlineDay === "" ? null : deadlineNum,
                segment_2_mapping_type:
                    segment2MappingType as TUpdateDealRequest["segment_2_mapping_type"],
                segment_4_mapping_type:
                    segment4MappingType as TUpdateDealRequest["segment_4_mapping_type"],
                segment_5_mapping_type:
                    segment5MappingType as TUpdateDealRequest["segment_5_mapping_type"],
                segment_6_mapping_type:
                    segment6MappingType as TUpdateDealRequest["segment_6_mapping_type"],
                segment_7_mapping_type:
                    segment7MappingType as TUpdateDealRequest["segment_7_mapping_type"],
            });
            if (nameEditable) {
                setName(trimmedName);
                setSavedName(trimmedName);
            }
            setSavedIsActive(isActive);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save deal");
        } finally {
            setSaving(false);
        }
    };

    const userOptions = users.map((u) => ({
        id: u.id,
        label: `${u.display_name} (${u.email})`,
    }));

    const selectClass =
        "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const inputClass = selectClass;
    const labelClass = "text-sm font-medium text-muted-foreground";
    const readOnlyClass = "text-sm font-medium";

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Editable by Product Owner only: Name */}
                <div>
                    <label className={labelClass} htmlFor="deal-name">
                        Name
                    </label>
                    {nameEditable ? (
                        <>
                            <input
                                id="deal-name"
                                type="text"
                                className={cn(inputClass, !nameValid && "border-destructive")}
                                value={name}
                                maxLength={255}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Deal name"
                            />
                            {!nameValid && (
                                <p className="text-xs text-destructive mt-1">
                                    Name cannot be empty
                                </p>
                            )}
                        </>
                    ) : (
                        <p className={readOnlyClass}>{deal.name}</p>
                    )}
                </div>

                {/* Read-only: File Type */}
                <div>
                    <label className={labelClass}>File Type</label>
                    <p className={readOnlyClass}>
                        {dealFileTypeLabels[deal.dealFileType] ?? deal.dealFileType}
                    </p>
                </div>

                {/* Read-only: Start Period */}
                <div>
                    <label className={labelClass}>Start Period</label>
                    <p className={readOnlyClass}>{formatStartPeriod(deal.startPeriod)}</p>
                </div>

                {/* Editable: Status (activate/deactivate) */}
                <div>
                    <label className={labelClass} htmlFor="deal-status">
                        Status
                    </label>
                    <div className="flex items-center gap-2 pt-2">
                        <input
                            id="deal-status"
                            type="checkbox"
                            className="h-4 w-4 rounded border-input"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            disabled={!canEdit}
                        />
                        {/* The "as of" instant belongs to the persisted status, so an
                            unsaved toggle shows the bare label — there is no timestamp
                            for a status the server has not accepted yet. */}
                        <span className={readOnlyClass}>
                            {isActive === savedIsActive
                                ? formatDealStatusLabel({ ...deal, isActive })
                                : isActive
                                  ? "Active"
                                  : "Inactive"}
                        </span>
                    </div>
                </div>

                {/* Editable: Final Destination */}
                <div>
                    <label className={labelClass}>Final Destination</label>
                    <select
                        className={selectClass}
                        value={finalDestination}
                        onChange={(e) => setFinalDestination(e.target.value)}
                        disabled={!canEdit}
                    >
                        {finalDestinationOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Editable: Deadline Day */}
                <div>
                    <label className={labelClass}>Deadline Day</label>
                    <input
                        type="number"
                        className={cn(inputClass, !deadlineValid && "border-destructive")}
                        value={deadlineDay}
                        min={1}
                        max={28}
                        onChange={(e) =>
                            setDeadlineDay(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        placeholder="Day of month (1-28)"
                        disabled={!canEdit}
                    />
                    {!deadlineValid ? (
                        <p className="text-xs text-destructive mt-1">Must be between 1 and 28</p>
                    ) : deadlineWarning ? (
                        <p
                            className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                            data-testid="deadline-day-warning"
                        >
                            {deadlineWarning.message}
                        </p>
                    ) : null}
                </div>

                {/* Editable: Segment 2 Mapping Type */}
                <div>
                    <label className={labelClass}>Segment 2 Mapping Type</label>
                    <select
                        className={selectClass}
                        value={segment2MappingType}
                        onChange={(e) => setSegment2MappingType(e.target.value)}
                        disabled={!canEdit}
                    >
                        {segment2MappingTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Editable: Segment 4 Mapping Type */}
                <div>
                    <label className={labelClass}>Segment 4 Mapping Type</label>
                    <select
                        className={selectClass}
                        value={segment4MappingType}
                        onChange={(e) => setSegment4MappingType(e.target.value)}
                        disabled={!canEdit}
                    >
                        {segment4MappingTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Editable: Segment 5 Mapping Type */}
                <div>
                    <label className={labelClass}>Segment 5 Mapping Type</label>
                    <select
                        className={selectClass}
                        value={segment5MappingType}
                        onChange={(e) => setSegment5MappingType(e.target.value)}
                        disabled={!canEdit}
                    >
                        {segment5MappingTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Editable: Segment 6 Mapping Type */}
                <div>
                    <label className={labelClass}>Segment 6 Mapping Type</label>
                    <select
                        className={selectClass}
                        value={segment6MappingType}
                        onChange={(e) => setSegment6MappingType(e.target.value)}
                        disabled={!canEdit}
                    >
                        {segment6MappingTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Editable: Segment 7 Mapping Type */}
                <div>
                    <label className={labelClass}>Segment 7 Mapping Type</label>
                    <select
                        className={selectClass}
                        value={segment7MappingType}
                        onChange={(e) => setSegment7MappingType(e.target.value)}
                        disabled={!canEdit}
                    >
                        {segment7MappingTypes.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Finance Team - full width */}
            <div>
                <label className={labelClass}>Finance Team</label>
                {usersLoading ? (
                    <Loader size="sm" />
                ) : (
                    <MultiSelect
                        options={userOptions}
                        selectedIds={financeUserIds}
                        onChange={setFinanceUserIds}
                        placeholder="Select finance team members..."
                        disabled={!canEdit}
                    />
                )}
            </div>

            {/* Save button and status */}
            {canEdit && (
                <div className="flex items-center gap-4 pt-2">
                    <Button onClick={handleSave} disabled={saving || !deadlineValid || !nameValid}>
                        {saving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                            </>
                        )}
                    </Button>
                    {saveError && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            {saveError}
                        </p>
                    )}
                    {saveSuccess && (
                        <p className="text-sm text-green-600">Changes saved successfully.</p>
                    )}
                </div>
            )}
        </div>
    );
}
